/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { isLeaderOrAdmin } from '@/utils/permissions';

interface ModerationContextType {
  /** Whether moderation mode is currently active */
  isModerationMode: boolean;
  /** Toggle moderation mode on/off (only works for leaders/admins) */
  toggleModerationMode: () => void;
  /** Whether the current user can use moderation features */
  canModerate: boolean;
}

const ModerationContext = createContext<ModerationContextType | null>(null);

const STORAGE_KEY = 'spirit-of-shabbat-moderation-mode';

export function ModerationProvider({ children }: { children: ReactNode }) {
  const { authState } = useAuth();
  const [isModerationMode, setIsModerationMode] = useState(false);

  // Get user profile if authenticated
  const profile = authState.status === 'authenticated' ? authState.profile : null;
  const canModerate = isLeaderOrAdmin(profile);

  // Load persisted state on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'true' && canModerate) {
      setIsModerationMode(true);
    }
  }, [canModerate]);

  // Persist state changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(isModerationMode));
  }, [isModerationMode]);

  // If user loses moderation privileges, turn off moderation mode
  useEffect(() => {
    if (!canModerate && isModerationMode) {
      setIsModerationMode(false);
    }
  }, [canModerate, isModerationMode]);

  const toggleModerationMode = useCallback(() => {
    if (!canModerate) return;
    setIsModerationMode((prev) => !prev);
  }, [canModerate]);

  const effectiveIsModerationMode = isModerationMode && canModerate;

  const value = useMemo(
    () => ({
      isModerationMode: effectiveIsModerationMode,
      toggleModerationMode,
      canModerate,
    }),
    [effectiveIsModerationMode, toggleModerationMode, canModerate]
  );

  return <ModerationContext.Provider value={value}>{children}</ModerationContext.Provider>;
}

export function useModeration(): ModerationContextType {
  const context = useContext(ModerationContext);
  if (!context) {
    throw new Error('useModeration must be used within a ModerationProvider');
  }
  return context;
}
